import { useEffect, useState } from "react";

/** One line over the bottom of the screen, for the things that used to be a
 *  paragraph appearing halfway up a card: saved, sent, that did not work.
 *
 *  It is a module-level list rather than a context so that a handler deep in a
 *  page can say something without the page having to thread a prop down to it.
 *  Anything that belongs to a particular field stays next to that field. */

export type ToastKind = "plain" | "good" | "bad";
type Action = { label: string; run: () => void };
type Toast = { id: number; text: string; kind: ToastKind; action?: Action; leaving?: boolean };

let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function publish() {
  for (const listener of listeners) listener();
}

function drop(id: number) {
  toasts = toasts.map((t) => (t.id === id ? { ...t, leaving: true } : t));
  publish();
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    publish();
  }, 200);
}

export function toast(text: string, kind: ToastKind = "plain", ms = 3200, action?: Action) {
  const id = nextId++;
  toasts = [...toasts, { id, text, kind, action }];
  publish();
  setTimeout(() => drop(id), ms);
}

export function ToastHost() {
  const [, force] = useState(0);
  useEffect(() => {
    const listener = () => force((n) => n + 1);
    listeners.add(listener);
    return () => void listeners.delete(listener);
  }, []);

  if (toasts.length === 0) return null;
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast${t.kind === "bad" ? " toast-bad" : t.kind === "good" ? " toast-good" : ""}${
            t.leaving ? " toast-out" : ""
          }`}
          onClick={() => drop(t.id)}
        >
          <span className="grow">{t.text}</span>
          {t.action && (
            <button
              className="toast-action"
              onClick={(e) => {
                e.stopPropagation();
                drop(t.id);
                t.action?.run();
              }}
            >
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
